import cx from 'classnames';
import {
  HelpOnboardingTooltip,
  ScheduleAssignmentOnboardingTooltip,
} from 'components/admin/onboarding/OnboardingTooltip';
import { Anchor, Button } from 'components/admin/ui';
import { useAuthContext } from 'components/auth';
import { AsyncImage, KebabMenu } from 'components/ui';
import { useWhiteLabelContext } from 'components/white-label';
import { EthenaIcon } from 'components/white-label/';
import {
  ChevronRightDouble,
  GraduationHat02,
  HelpCircle,
  LogOut01,
  Tool02,
} from 'ethena-icons';
import { PageName } from 'lib/constants';
import { ADMIN_PAGES } from 'lib/datasets/pages';
import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import { buildQueryParamLink } from 'lib/helpers/urlHelper';
import { useToggle } from 'lib/hooks';
import { Organization } from 'lib/types/company';
import _ from 'lodash';
import { useRouter } from 'next/router';
import {
  ElementType,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import store from 'store2';
import css from './Navigation.module.scss';

interface INavigationLinkProps {
  href: string;
  icon: ElementType;
  label: string;
  isNew?: boolean;
}

const NavigationLink = ({
  href,
  icon: Icon,
  label,
  isNew = false,
}: INavigationLinkProps) => (
  <div className={css.navigationLinkContainer}>
    <Anchor aria-label={label} className={css.navigationAnchor} href={href}>
      <div className={css.iconContainer}>
        <Icon height={20} width={20} />
      </div>
      <span className={css.navigationLabel}>{label}</span>
      {isNew && <div className={css.newPill}>New</div>}
    </Anchor>
  </div>
);

const HelpKebabOptions = ({
  handleToggleNavigationAnimation,
  showNavigationAnimation,
}) => {
  const { helpCenterUrl, howItWorksUrl, legalAndPrivacyUrl } =
    useWhiteLabelContext();

  return (
    <KebabMenu.Options placement="right-start">
      <KebabMenu.Option>
        <Anchor
          aria-label="Help center"
          className={css.noLinkStyle}
          href={helpCenterUrl}
          target="_blank"
        >
          Help Center
        </Anchor>
      </KebabMenu.Option>
      <KebabMenu.Option>
        <Anchor
          aria-label="How it works"
          className={css.noLinkStyle}
          href={howItWorksUrl}
          target="_blank"
        >
          How It Works
        </Anchor>
      </KebabMenu.Option>
      <KebabMenu.Option>
        <Anchor
          aria-label="Legal and privacy"
          className={css.noLinkStyle}
          href={legalAndPrivacyUrl}
          target="_blank"
        >
          Legal &amp; Privacy
        </Anchor>
      </KebabMenu.Option>
      <KebabMenu.Option onClick={handleToggleNavigationAnimation}>
        {showNavigationAnimation
          ? 'Hide Navigation Titles'
          : 'Show Navigation Titles'}
      </KebabMenu.Option>
    </KebabMenu.Options>
  );
};

export const Navigation = () => {
  const { pathname } = useRouter();

  const { getIsPageAccessible, isInternalAdmin, session } = useAuthContext();
  const { organization, organizationTheme, homePageName } =
    useWhiteLabelContext();

  const nodeRef = useRef<HTMLElement>(null);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isNavigationHovered, setIsNavigationHovered] = useState(false);
  const [showNavigationAnimation, toggleShowNavigationAnimation] =
    useToggle(true);
  const { ssoConnection } = session.configurations;

  const getIsCurrentPath = (path: string) => _.includes(pathname, path);
  const homePageUrl = _.find(ADMIN_PAGES, (x) => x.name === homePageName)?.path;

  const handleMouseOut: MouseEventHandler = () => {
    setIsNavigationHovered(false);
  };

  const handleMouseOver: MouseEventHandler = (e) => {
    if (nodeRef.current?.contains(e.target as Node)) {
      setIsNavigationHovered(true);
    }
  };

  const handleToggleNavigationAnimation = () => {
    toggleShowNavigationAnimation();
    store.set(
      'navigationAnimationDisabled',
      showNavigationAnimation ? 'true' : 'false'
    );
  };

  useEffect(() => {
    if (store.get('navigationAnimationDisabled') === 'true') {
      toggleShowNavigationAnimation();
    }
  }, []);

  const renderNavigationLink = (page: (typeof ADMIN_PAGES)[number]) => {
    if (!getIsPageAccessible(page.name)) {
      return null;
    }
    switch (page.name) {
      case PageName.ADMIN_DASHBOARD:
        return (
          <ScheduleAssignmentOnboardingTooltip
            isHidden={isNavigationHovered || isHelpMenuOpen}
            key={page.name}
          >
            <li aria-current={getIsCurrentPath(page.path)}>
              <NavigationLink
                icon={page.icon}
                href={page.path}
                label={page.getLabel({ organization })}
              />
            </li>
          </ScheduleAssignmentOnboardingTooltip>
        );
      default:
        return (
          <li key={page.name} aria-current={getIsCurrentPath(page.path)}>
            <NavigationLink
              icon={page.icon}
              href={page.path}
              label={page.getLabel({ organization })}
            />
          </li>
        );
    }
  };

  return (
    <nav
      className={cx(css.navigation, {
        [css.navigationAnimation]: showNavigationAnimation,
        [css.navigationHover]:
          (isHelpMenuOpen || isMoreMenuOpen) && showNavigationAnimation,
      })}
      onMouseOut={handleMouseOut}
      onMouseOver={handleMouseOver}
      ref={nodeRef}
    >
      <Anchor aria-label="Home Page" href={homePageUrl}>
        {organization === Organization.LIFE_LABS ? (
          <AsyncImage
            alt="Logo"
            className={css.logo}
            src={organizationTheme.logoUrl}
            width={30}
          />
        ) : (
          <EthenaIcon className={css.logo} width={40} height={40} />
        )}
      </Anchor>

      <div className={css.linksContainer}>
        <div>
          <ul className={css.links}>
            {_.map(ADMIN_PAGES, renderNavigationLink)}
          </ul>
          {getIsPageAccessible(PageName.TRAINING_CENTER) && (
            <ul className={cx(css.links, css.bordered, css.navigationFull)}>
              <li>
                <NavigationLink
                  href={buildQueryParamLink('/learning', {
                    sso: ssoConnection,
                  })}
                  icon={GraduationHat02}
                  label="Learning Center"
                />
              </li>
            </ul>
          )}
          <ul className={cx(css.links, css.bordered, css.navigationMore)}>
            <li
              className={cx({
                [css.linkHover]: isMoreMenuOpen && showNavigationAnimation,
              })}
            >
              <KebabMenu onOpen={setIsMoreMenuOpen}>
                <KebabMenu.Toggle>
                  <Button className={css.kebabButton} variant="transparent">
                    <div className={css.kebabButtonContents}>
                      <div className={css.iconContainer}>
                        <ChevronRightDouble height={20} width={20} />
                      </div>
                      <span className={css.navigationLabel}>More</span>
                    </div>
                  </Button>
                </KebabMenu.Toggle>
                <KebabMenu.Options placement="right-start">
                  <KebabMenu.Option>
                    <Anchor
                      aria-label="Learning Center"
                      className={css.noLinkStyle}
                      href="/learning"
                    >
                      Learning Center
                    </Anchor>
                  </KebabMenu.Option>
                  {isInternalAdmin && (
                    <KebabMenu.Option>
                      <Anchor
                        aria-label="Internal Tools"
                        className={css.noLinkStyle}
                        href="/internal"
                      >
                        Internal Tools
                      </Anchor>
                    </KebabMenu.Option>
                  )}
                  <KebabMenu.Option>
                    <KebabMenu onOpen={setIsHelpMenuOpen}>
                      <KebabMenu.Toggle>
                        <div className="pr-3 w-100">Help</div>
                      </KebabMenu.Toggle>
                      <HelpKebabOptions
                        handleToggleNavigationAnimation={
                          handleToggleNavigationAnimation
                        }
                        showNavigationAnimation={showNavigationAnimation}
                      />
                    </KebabMenu>
                  </KebabMenu.Option>
                  <KebabMenu.Option>
                    <Anchor
                      aria-label="Logout"
                      className={css.noLinkStyle}
                      href={`${getEnv(EnvVariable.API_PATHNAME)}/logout`}
                    >
                      Logout
                    </Anchor>
                  </KebabMenu.Option>
                </KebabMenu.Options>
              </KebabMenu>
            </li>
          </ul>
        </div>

        <ul className={cx(css.links, css.navigationFull)}>
          {isInternalAdmin && (
            <li>
              <NavigationLink
                href="/internal"
                icon={Tool02}
                label="Internal Tools"
              />
            </li>
          )}
          <HelpOnboardingTooltip
            isHidden={isNavigationHovered || isHelpMenuOpen}
          >
            <li
              className={cx({
                [css.linkHover]: isHelpMenuOpen && showNavigationAnimation,
              })}
            >
              <KebabMenu onOpen={setIsHelpMenuOpen}>
                <KebabMenu.Toggle>
                  <Button className={css.kebabButton} variant="transparent">
                    <div className={css.kebabButtonContents}>
                      <div className={css.iconContainer}>
                        <HelpCircle height={20} width={20} />
                      </div>
                      <span className={css.navigationLabel}>Help</span>
                    </div>
                  </Button>
                </KebabMenu.Toggle>
                <HelpKebabOptions
                  handleToggleNavigationAnimation={
                    handleToggleNavigationAnimation
                  }
                  showNavigationAnimation={showNavigationAnimation}
                />
              </KebabMenu>
            </li>
          </HelpOnboardingTooltip>
          <li>
            <NavigationLink
              href={`${getEnv(EnvVariable.API_PATHNAME)}/logout`}
              icon={LogOut01}
              label="Logout"
            />
          </li>
        </ul>
      </div>
    </nav>
  );
};
